import React, { useState, useCallback } from 'react';
import { Grid, Header, Message, Modal, Button, Icon } from 'semantic-ui-react';
import ManagementHeader from '../../../common/components/ManagementHeader';
import { useSharedData, useActorZone } from '../../hooks/core/useZoneHooks';
import { ActorsListEnh } from '../lists';
import { ActorProfileForm } from '../forms';

/**
 * ActorManagement-enh component
 * Enhanced version of ActorManagement with improved UI matching BookCreator components
 * Manages voice actors
 * @returns {JSX.Element} ActorManagement-enh component
 */
const ActorManagementEnh = () => {
  const sharedData = useSharedData();
  const actorZone = useActorZone();

  // Extract data from hooks
  const {
    actors,
    portfolios,
    selections,
    actorProfiles
  } = sharedData;

  const { selectedActor, actions: actorActions } = actorZone;

  // Map to interface
  const actorsArray = Array.isArray(actors) ? actors : [];
  const selectionsObj = selections || {};
  const actorId = selectionsObj.actorId;

  // Get the selected actor profile
  const selectedActorProfile = actorId ? actorProfiles[actorId] : null;

  // Get functions from shared data
  const fetchActors = sharedData.actions.fetchActors;
  const fetchPortfolios = sharedData.actions.fetchPortfolios;
  const setActorSelection = sharedData.actions.setActorSelection;
  const fetchActorProfile = sharedData.actions.fetchActorProfile;

  // State for editing mode
  const [isEditing, setIsEditing] = useState(!!actorId);

  // State for delete confirmation modal
  const [deleteActorModalOpen, setDeleteActorModalOpen] = useState(false);
  const [actorToDelete, setActorToDelete] = useState(null);

  // State for messages
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Handle actor selection
  const handleActorSelect = useCallback((actor) => {
    if (actor && actor.uuid) {
      // Set the selection
      setActorSelection(actor.uuid);

      // Fetch the actor profile
      fetchActorProfile(actor.uuid);

      // Fetch portfolios for this actor
      fetchPortfolios(actor.uuid);

      setIsEditing(true);
    }
  }, [setActorSelection, fetchActorProfile, fetchPortfolios]);

  // Handle delete actor
  const handleDeleteActor = useCallback((actor) => {
    // Show the delete confirmation modal
    setActorToDelete(actor);
    setDeleteActorModalOpen(true);
  }, []);

  // Handler for confirming actor deletion
  const confirmDeleteActor = useCallback(async () => {
    try {
      console.log('[VA_ACTOR_MGMT] Deleting actor:', actorToDelete);

      // TODO: Implement actor deletion API call
      // await actorActions.deleteActor(actorToDelete.uuid);

      // Close the modal
      setDeleteActorModalOpen(false);
      setActorToDelete(null);

      // If the deleted actor was selected, clear the selection
      if (selectedActorProfile && selectedActorProfile.uuid === actorToDelete.uuid) {
        setActorSelection(null);
        setIsEditing(false);
      }

      // Refresh the actors list
      fetchActors();

      setSuccessMessage('Actor deleted successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('[VA_ACTOR_MGMT] Error deleting actor:', error);
      setErrorMessage('Error deleting actor: ' + error.message);
      setTimeout(() => setErrorMessage(''), 5000);
    }
  }, [actorToDelete, selectedActorProfile, setActorSelection, fetchActors]);

  // Handle save
  const handleSaveProfile = useCallback((updatedProfile) => {
    // Refresh data after save
    if (updatedProfile && updatedProfile.uuid) {
      // Set the selection first - this already fetches Actor data and profile
      setActorSelection(updatedProfile.uuid);

      // Only fetch portfolios explicitly - don't re-fetch Actor data
      fetchPortfolios(updatedProfile.uuid);

      setIsEditing(true);

      // Show success message
      setSuccessMessage('Voice actor saved successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
    }
  }, [setActorSelection, fetchPortfolios]);

  // Handle new actor
  const handleNewActor = useCallback(() => {
    setActorSelection(null);
    setIsEditing(false);
  }, [setActorSelection]);

  return (
    <ManagementHeader
      title="Profile Management"
      error={errorMessage}
      isLoading={false}
      width="1024px"
      isEditing={isEditing}
    >
      <Grid>
        <Grid.Row>
          <Grid.Column width={6}>
            <ActorsListEnh
              onActorSelect={handleActorSelect}
              onDeleteActor={handleDeleteActor}
            />

            <Button
              primary
              icon
              labelPosition="left"
              onClick={handleNewActor}
              style={{ marginTop: '10px' }}
            >
              <Icon name="add" />
              New Voice Actor
            </Button>
          </Grid.Column>

          <Grid.Column width={10}>
            {successMessage && (
              <Message positive>
                <Message.Header>Success</Message.Header>
                <p>{successMessage}</p>
              </Message>
            )}

            <Header as="h3" dividing>
              Voice Actor Details
              {selectedActorProfile && selectedActorProfile.firstName && (
                <span className="selectedItemTitle"> - {selectedActorProfile.firstName} {selectedActorProfile.lastName}</span>
              )}
            </Header>

            {!selectedActorProfile && !isEditing ? (
              <div className="va-empty-selection-message">
                <p>Select a voice actor from the list to view or edit their details, or click "New Voice Actor" to create one.</p>
              </div>
            ) : (
              <ActorProfileForm
                onSave={handleSaveProfile}
                selectedActorProfile={selectedActorProfile}
                actorId={actorId}
              />
            )}
          </Grid.Column>
        </Grid.Row>
      </Grid>

      {/* Delete Actor Confirmation Modal */}
      <Modal open={deleteActorModalOpen} size="small">
        <Modal.Header>Delete Voice Actor</Modal.Header>
        <Modal.Content>
          <p>Are you sure you want to delete this voice actor? This will also delete all portfolios and characters associated with them. This action cannot be undone.</p>
          {actorToDelete && (
            <Message warning>
              <Message.Header>You are about to delete:</Message.Header>
              <p><strong>{actorToDelete.name}</strong> ({actorToDelete.email || 'N/A'})</p>
            </Message>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button negative onClick={confirmDeleteActor}>
            Delete
          </Button>
          <Button onClick={() => setDeleteActorModalOpen(false)}>
            Cancel
          </Button>
        </Modal.Actions>
      </Modal>
    </ManagementHeader>
  );
};

export default ActorManagementEnh;
